import {
  type CreatedRoomInvite,
  type InviteExpiration,
  type Room,
  type RoomInvite,
} from './rooms';
import type { Friend } from './friends';
import { escapeSignInText as escape } from './sign-in-form';
import type { ClubInvite, CreatedClubInvite } from './clubs';

interface HostInviteControlsOptions {
  room: Pick<Room, 'createdBy' | 'status'> & { expiresAt: number | null };
  currentUserId: string | null;
  invites: Array<RoomInvite | ClubInvite>;
  latestInvite: CreatedRoomInvite | CreatedClubInvite | null;
  selectedExpiration: InviteExpiration;
  origin: string;
  now?: number;
  friends?: Friend[];
  selectedFriendUserId?: string;
  latestInviteFriendName?: string;
}

function formatRemaining(ms: number): string {
  const totalMinutes = Math.max(0, Math.ceil(ms / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${minutes}m`;
}

function lastsUntilWorkoutEnds(inviteExpiresAt: number | null, workoutExpiresAt: number | null): boolean {
  return inviteExpiresAt !== null && workoutExpiresAt !== null && inviteExpiresAt >= workoutExpiresAt;
}

export function getInviteStatusText(
  invite: RoomInvite | ClubInvite,
  workoutExpiresAt: number | null,
  now = Date.now(),
): string {
  if (invite.status === 'available') {
    if (invite.expiresAt === null) return 'Available until used or revoked';
    return lastsUntilWorkoutEnds(invite.expiresAt, workoutExpiresAt)
      ? 'Available until workout ends'
      : `Available for ${formatRemaining(invite.expiresAt - now)}`;
  }
  if (invite.status === 'redeemed') return 'Used';
  if (invite.status === 'revoked') return 'Revoked';
  if (invite.status === 'room-closed') return 'Workout closed';
  return 'Expired';
}

export function renderHostInviteControls({
  room,
  currentUserId,
  invites,
  latestInvite,
  selectedExpiration,
  origin,
  now = Date.now(),
  friends = [],
  selectedFriendUserId = '',
  latestInviteFriendName = '',
}: HostInviteControlsOptions): string {
  if (!currentUserId || room.createdBy !== currentUserId || room.status !== 'active') return '';

  const latestInviteUrl = latestInvite
    ? `${origin}/?invite=${encodeURIComponent(latestInvite.token)}`
    : null;
  const latestInviteMarkup = latestInviteUrl && latestInvite
    ? `
      <div class="invite-link-row">
        <input id="invite-link" readonly value="${latestInviteUrl}" />
        <button id="copy-invite-btn" class="btn" type="button">Copy</button>
      </div>
      ${latestInviteFriendName ? `<p class="card-copy">Copy this link and send it to <strong>${escape(latestInviteFriendName)}</strong>.</p>` : ''}
      <p class="card-copy">${latestInvite.expiresAt === null ? 'Valid until used or revoked.' : lastsUntilWorkoutEnds(latestInvite.expiresAt, room.expiresAt)
        ? 'Valid until the workout ends.'
        : `Expires in ${formatRemaining(latestInvite.expiresAt - now)}.`} This single-use link is shown only once.</p>
    `
    : '';
  const inviteRows = invites.length
    ? invites.map((invite) => `
      <li class="invite-list-item">
        <span>${getInviteStatusText(invite, room.expiresAt, now)}</span>
        ${invite.status === 'available'
          ? `<button class="btn-secondary revoke-invite-btn" type="button" data-invite-id="${invite.id}">Revoke</button>`
          : ''}
      </li>
    `).join('')
    : '<li class="invite-list-empty">No invitations created yet.</li>';

  return `
    <section class="share-box">
      <h3>Invite A Player</h3>
      ${friends.length ? `<div class="friend-invite-choice">
        <label for="invite-friend">Invite a friend</label>
        <select id="invite-friend">
          <option value="">Anyone with the link</option>
          ${friends.map((friend) => `<option value="${escape(friend.userId)}"${friend.userId === selectedFriendUserId ? ' selected' : ''}>${escape(friend.username)}</option>`).join('')}
        </select>
        <p class="card-copy">Create a link, then send it to your friend.</p>
      </div>` : ''}
      <div class="invite-create-controls">
        <label class="invite-expiration-label" for="invite-expiration">Expires</label>
        <select id="invite-expiration" aria-label="Invite expiration">
          <option value="unlimited"${selectedExpiration === 'unlimited' ? ' selected' : ''}>Unlimited</option>
          <option value="10m"${selectedExpiration === '10m' ? ' selected' : ''}>10m</option>
        </select>
        <button id="create-invite-btn" class="btn" type="button">Create Invite Link</button>
      </div>
      ${latestInviteMarkup}
      <ul class="invite-list">${inviteRows}</ul>
    </section>
  `;
}
